import { useEffect, useState } from 'react'
import { revalidateLogic, useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getFieldError } from '@/components/tareas/forms'
import { useCrearTarea } from '@/core/hooks/useTareas'
import { tareasService } from '@/core/services/tareasService'
import {
  CLAVE_FISCAL_VACIA,
  CONDICIONES_INSCRIPCION,
  IMPUESTOS_INSCRIPCION,
  descripcionInscripcion,
  subtareasInscripcion,
  tituloInscripcion,
  type ClaveFiscalValores,
  type ImpuestoInscripcion,
} from '@/core/lib/inscripcion'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { useVault } from '@/hooks/useVault'
import { usePermissions } from '@/hooks/usePermissions'
import { useSupabase } from '@/lib/supabase-context'
import { formatearCUIT, validarCUIT } from '@/lib/utils'
import { PROVINCIAS_ARGENTINA } from '@/workspaces/contable/types/faceta'
import { CamposClaveFiscal, ConCajaFuerteAbierta } from './ClaveFiscalInscripcion'

const IDS_IMPUESTOS = IMPUESTOS_INSCRIPCION.map(({ id }) => id) as [
  ImpuestoInscripcion,
  ...ImpuestoInscripcion[],
]

const inscripcionSchema = z
  .object({
    cuit: z.string(),
    condicion: z.enum(CONDICIONES_INSCRIPCION).nullable(),
    impuestos: z.array(z.enum(IDS_IMPUESTOS)),
    provinciaRentas: z.string().nullable(),
    actividad: z.string().nullable(),
    comentarios: z.string().nullable(),
    asignado_a: z.string().nullable(),
    fecha_limite: z.string().nullable(),
  })
  .superRefine((valores, ctx) => {
    const cuit = validarCUIT(valores.cuit)
    if (!cuit.valido) {
      ctx.addIssue({ code: 'custom', path: ['cuit'], message: cuit.error ?? 'El CUIT no es válido' })
    }
    if (!valores.condicion) {
      ctx.addIssue({
        code: 'custom',
        path: ['condicion'],
        message: 'el campo "condición" de la inscripción es obligatorio',
      })
    }
    if (valores.impuestos.includes('rentas_local') && !valores.provinciaRentas) {
      ctx.addIssue({
        code: 'custom',
        path: ['provinciaRentas'],
        message: 'el campo "provincia" de la inscripción es obligatorio con Rentas local',
      })
    }
  })

type InscripcionFormValues = z.infer<typeof inscripcionSchema>

interface InscripcionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Alta de una tarea de nueva inscripción: el alta de un futuro cliente en uno o más impuestos.
 * La tarea sale urgente, con el CUIT guardado y una subtarea por impuesto elegido. La clave fiscal es
 * opcional: se guarda cifrada después de crear la tarea, y pasa a la Caja Fuerte del cliente en el alta.
 */
export function InscripcionFormDialog({ open, onOpenChange }: InscripcionFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Nueva inscripción</SheetTitle>
            <SheetDescription>
              Tarea urgente para inscribir a un futuro cliente. Al completarla, Menta te lleva al alta
              del cliente con el CUIT cargado.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <InscripcionForm
              onCreada={() => onOpenChange(false)}
              onCancel={() => handleOpenChange(false)}
              onDirtyChange={setIsFormDirty}
            />
          </div>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}

interface InscripcionFormProps {
  onCreada: () => void
  onCancel: () => void
  onDirtyChange: (dirty: boolean) => void
}

function InscripcionForm({ onCreada, onCancel, onDirtyChange }: InscripcionFormProps) {
  const { supabase } = useSupabase()
  const crearTarea = useCrearTarea()
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })
  const { rolMultiTenant, emailUsuario, puede } = usePermissions()
  const puedeDelegar = ['owner', 'admin', 'profesional'].includes(rolMultiTenant ?? '')
  // El asistente queda asignado a la tarea, así que con crearCliente puede cargar la clave (E2).
  const puedeCargarClave = rolMultiTenant !== 'solo_lectura' && puede.crearCliente === true
  const vault = useVault()
  const [claveFiscal, setClaveFiscal] = useState<ClaveFiscalValores>(CLAVE_FISCAL_VACIA)

  const form = useForm({
    defaultValues: {
      cuit: '',
      condicion: null,
      impuestos: [],
      provinciaRentas: null,
      actividad: null,
      comentarios: null,
      asignado_a: puedeDelegar ? null : (emailUsuario ?? null),
      fecha_limite: null,
    } as InscripcionFormValues,
    // La provincia depende de los impuestos. La validación dinámica limpia su error al destildar
    // Rentas local; con `onSubmit` el error queda y el botón deja de responder.
    validationLogic: revalidateLogic(),
    validators: { onDynamic: inscripcionSchema },
    onSubmit: async ({ value }) => {
      const cuit = value.cuit.replace(/\D/g, '')
      const tarea = await crearTarea
        .mutateAsync({
          titulo: tituloInscripcion(formatearCUIT(cuit)),
          descripcion: descripcionInscripcion({
            condicion: value.condicion!,
            actividad: value.actividad,
            comentarios: value.comentarios,
          }),
          asignado_a: value.asignado_a,
          prioridad: 'urgente',
          fecha_limite: value.fecha_limite,
          tipo: 'inscripcion',
          cuit,
        })
        // `useCrearTarea` ya muestra el error. El formulario queda abierto para reintentar.
        .catch(() => null)
      if (!tarea) return

      try {
        await tareasService.crearSubtareasEnLote(
          supabase!,
          tarea.id,
          tarea.organizacion_id,
          subtareasInscripcion(value.impuestos, value.provinciaRentas),
        )
      } catch (error) {
        console.error('Error al crear el checklist de la inscripción:', error)
        toast.warning(
          'El checklist de impuestos de la inscripción no se pudo crear y se carga desde el detalle de la tarea.',
        )
      }
      if (puedeCargarClave && claveFiscal.clave) {
        try {
          await vault.guardarClaveInscripcion(tarea.id, {
            organismo: claveFiscal.organismo,
            usuario: claveFiscal.usuario.trim() || cuit,
            clave: claveFiscal.clave,
          })
        } catch (error) {
          console.error('Error al guardar la clave fiscal de la inscripción:', error)
          toast.warning('La clave fiscal de la inscripción no se pudo guardar y se carga desde el detalle de la tarea.')
        }
      }
      toast.success('Inscripción creada')
      onCreada()
    },
  })

  const isDirty = useStore(form.store, (state) => state.isDirty)
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])
  useEffect(() => () => onDirtyChange(false), [onDirtyChange])

  const impuestos = useStore(form.store, (state) => state.values.impuestos)
  const guardando = useStore(form.store, (state) => state.isSubmitting)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      <form.Field name="cuit">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>CUIT *</Label>
            <Input
              id={field.name}
              inputMode="numeric"
              placeholder="20-12345678-9"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="condicion">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Condición *</Label>
            <Select
              value={field.state.value ?? undefined}
              onValueChange={(value) => field.handleChange(value as InscripcionFormValues['condicion'])}
            >
              <SelectTrigger id={field.name}>
                <SelectValue placeholder="Elegí la condición" />
              </SelectTrigger>
              <SelectContent>
                {CONDICIONES_INSCRIPCION.map((condicion) => (
                  <SelectItem key={condicion} value={condicion}>
                    {condicion}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="impuestos">
        {(field) => (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Impuestos en los que se inscribe</legend>
            <p className="text-xs text-muted-foreground">Cada uno queda como un ítem del checklist de la tarea.</p>
            <div className="grid grid-cols-2 gap-2">
              {IMPUESTOS_INSCRIPCION.map(({ id, label }) => (
                <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={field.state.value.includes(id)}
                    onCheckedChange={(checked) =>
                      field.handleChange(
                        checked
                          ? [...field.state.value, id]
                          : field.state.value.filter((elegido) => elegido !== id),
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </form.Field>

      {impuestos.includes('rentas_local') && (
        <form.Field name="provinciaRentas">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Provincia de Rentas *</Label>
              <Select
                value={field.state.value ?? undefined}
                onValueChange={(value) => field.handleChange(value)}
              >
                <SelectTrigger id={field.name}>
                  <SelectValue placeholder="Elegí la provincia" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCIAS_ARGENTINA.map((provincia) => (
                    <SelectItem key={provincia} value={provincia}>
                      {provincia}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>
              )}
            </div>
          )}
        </form.Field>
      )}

      {puedeCargarClave && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Clave fiscal</legend>
          <p className="text-xs text-muted-foreground">
            Opcional. Se guarda cifrada y pasa a la Caja Fuerte del cliente cuando lo das de alta.
          </p>
          <ConCajaFuerteAbierta vault={vault} para="cargar la clave">
            <CamposClaveFiscal valores={claveFiscal} onChange={setClaveFiscal} />
          </ConCajaFuerteAbierta>
        </fieldset>
      )}

      <form.Field name="actividad">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Actividad</Label>
            <Input
              id={field.name}
              placeholder="Ej.: venta de indumentaria"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value || null)}
              onBlur={field.handleBlur}
            />
          </div>
        )}
      </form.Field>

      <form.Field name="comentarios">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Comentarios</Label>
            <Textarea
              id={field.name}
              rows={3}
              placeholder="Lo que haga falta aclarar"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value || null)}
              onBlur={field.handleBlur}
            />
          </div>
        )}
      </form.Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form.Field name="asignado_a">
          {(field) => (
            <div className="space-y-2">
              <Label>Asignado a</Label>
              {puedeDelegar ? (
                <Select
                  value={field.state.value ?? '_none'}
                  onValueChange={(value) => field.handleChange(value === '_none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin asignar</SelectItem>
                    {usuarios.map((usuario) => (
                      <SelectItem key={usuario.id} value={usuario.email}>
                        {usuario.nombre || usuario.email.split('@')[0]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={emailUsuario ?? ''} disabled className="text-muted-foreground" />
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="fecha_limite">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Fecha límite</Label>
              <Input
                id={field.name}
                type="date"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
              />
            </div>
          )}
        </form.Field>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={guardando}>
          {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear inscripción
        </Button>
      </div>
    </form>
  )
}
